import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, collection, getDocs, query } from 'firebase/firestore';
import { db, appId } from '../firebase/config';
import { defaultSettings } from '../constants/settings';

export function useBookingData() {
    const [bookingSettings, setBookingSettings] = useState(null);
    const [dailyAvailability, setDailyAvailability] = useState({});
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    // 1. Carregar Configurações
    useEffect(() => {
        const settingsRef = doc(db, `artifacts/${appId}/public/data/configuracoes`, 'sistema');
        const unsub = onSnapshot(settingsRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const merged = {
                    ...defaultSettings,
                    ...data,
                    horarios: {
                        almoco: { ...defaultSettings.horarios.almoco, ...data.horarios?.almoco },
                        jantar: { ...defaultSettings.horarios.jantar, ...data.horarios?.jantar }
                    },
                    descontos: {
                        ...defaultSettings.descontos,
                        ...(data.descontos || {}),
                        escaloes: (data.descontos?.escaloes || []).map((e, i) => ({
                            ...e, 
                            id: e.id || `esc-${i}`,
                            quantidade: parseInt(e.quantidade || e.atePessoas || 0),
                            percentagem: parseInt(e.percentagem || 0)
                        })),
                        agendamento: { ...defaultSettings.descontos.agendamento, ...(data.descontos?.agendamento || {}) }
                    }
                };
                setBookingSettings(merged);
            } else {
                setBookingSettings(defaultSettings);
            }
            setLoading(false);
        });
        return () => unsub();
    }, []);

    // 2. Carregar Eventos
    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/public/data/eventos`));
        const unsub = onSnapshot(q, (snap) => setEvents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
        return () => unsub();
    }, []);

    // 3. Disponibilidade
    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/public/data/disponibilidadeDiaria`));
        const unsub = onSnapshot(q, (snap) => {
            const data = {};
            snap.forEach(doc => { data[doc.id] = doc.data(); });
            setDailyAvailability(data);
        });
        return () => unsub();
    }, []);

    // --- NOVA LÓGICA: NIVELAR POR BAIXO ---
    const calculateDiscountBreakdown = useCallback((dateStr, time, numPessoas) => {
        if (!bookingSettings || !bookingSettings.descontos || !bookingSettings.descontos.ativo || !numPessoas) return [];

        // 1. Verificações Básicas
        const hasEvent = events.find(e => e.dataEvento === dateStr && e.ativo);
        if (hasEvent) return [{ percent: 0, count: numPessoas }];

        const dateObj = new Date(dateStr);
        const { agendamento } = bookingSettings.descontos;
        if (agendamento) {
            const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
            const days = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
            const weekNum = Math.ceil(dateObj.getDate() / 7);
            const weekKey = weekNum > 4 ? 'semana5' : `semana${weekNum}`;
            const hora = parseInt(time.split(':')[0]);
            const isJantar = hora >= 18;
            
            if (
                (agendamento.mesesAtivos && !agendamento.mesesAtivos[months[dateObj.getMonth()]]) ||
                (agendamento.diasSemana && !agendamento.diasSemana[days[dateObj.getDay()]]) ||
                (agendamento.semanasMes && !agendamento.semanasMes[weekKey]) ||
                (agendamento.turnosServico && !agendamento.turnosServico[isJantar ? 'jantar' : 'almoco'])
            ) {
                return [{ percent: 0, count: numPessoas }];
            }
        }

        // 2. Determinar Ocupação
        const hora = parseInt(time.split(':')[0]);
        const serviceKey = hora >= 18 ? 'jantar' : 'almoco';
        let turnNum = null;
        if (bookingSettings.horarios[serviceKey]?.turnosAtivos) {
            if (time < bookingSettings.horarios[serviceKey].turno1.fim) turnNum = 1; else turnNum = 2;
        }

        const ocupacaoKey = `lugaresReservados_${serviceKey}${turnNum ? `_t${turnNum}` : ''}`;
        const ocupadosTotal = dailyAvailability[dateStr]?.[ocupacaoKey] || 0;

        // Limite Global
        const limiteGlobal = bookingSettings.descontos.limiteReservasOnline[serviceKey].total;
        if ((ocupadosTotal + numPessoas) > limiteGlobal) {
            return []; // Indisponível
        }

        // 3. CALCULAR O MENOR DESCONTO APLICÁVEL (Downgrade Logic)
        let pessoasRestantes = numPessoas;
        let posicaoAtual = ocupadosTotal; 
        let percentagesTouched = []; // Lista de % que este grupo vai ocupar

        let inicioFatia = 0;
        const escaloes = (bookingSettings.descontos.escaloes || []).slice().sort((a, b) => b.percentagem - a.percentagem);
        
        const escaloesComIntervalo = escaloes.map(e => {
            const fatia = { ...e, inicio: inicioFatia, fim: inicioFatia + e.quantidade };
            inicioFatia += e.quantidade;
            return fatia;
        });

        for (const escalao of escaloesComIntervalo) {
            if (pessoasRestantes <= 0) break;

            const overlapStart = Math.max(posicaoAtual, escalao.inicio);
            const overlapEnd = Math.min(posicaoAtual + pessoasRestantes, escalao.fim);
            const lugaresNestaFatia = Math.max(0, overlapEnd - overlapStart);

            if (lugaresNestaFatia > 0) {
                percentagesTouched.push(escalao.percentagem);
                pessoasRestantes -= lugaresNestaFatia;
                posicaoAtual += lugaresNestaFatia;
            }
        }

        // Se sobrou gente fora dos escalões, tocam na fatia de 0%
        if (pessoasRestantes > 0) {
            percentagesTouched.push(0);
        }

        // REGRA DE OURO: O grupo todo recebe a PIOR percentagem encontrada
        const finalPercent = Math.min(...percentagesTouched);

        // Retorna formato simplificado: Todo o grupo com a percentagem final
        return [{ percent: finalPercent, count: numPessoas }];

    }, [bookingSettings, dailyAvailability, events]);

    // Função para o Calendário (Melhor possível para 1 pessoa)
    const getBestDiscountForTime = useCallback((dateStr, time) => {
        const breakdown = calculateDiscountBreakdown(dateStr, time, 1);
        if (!breakdown || breakdown.length === 0) return -1; 
        return breakdown[0].percent;
    }, [calculateDiscountBreakdown]);

    const getDayStatus = useCallback((dateParts) => {
        const dateStr = `${dateParts.ano}-${String(dateParts.mes).padStart(2, '0')}-${String(dateParts.dia).padStart(2, '0')}`;
        const event = events.find(e => e.dataEvento === dateStr && e.ativo);
        if (event) return { status: 'event', isEvent: true, eventName: event.nomeEvento };

        const discLunch = getBestDiscountForTime(dateStr, "13:00");
        const discDinner = getBestDiscountForTime(dateStr, "20:00");

        if (discLunch > 0 || discDinner > 0) return { status: 'promo', isEvent: false };
        if (discLunch === -1 && discDinner === -1) return { status: 'full', isEvent: false };

        return { status: 'available', isEvent: false };
    }, [events, getBestDiscountForTime]);

    return { 
        bookingSettings, loading, getDayStatus, getBestDiscountForTime, calculateDiscountBreakdown, events, dailyAvailability 
    };
}